import React from 'react';
import { Brain, MapPin, TrendingUp, Calendar } from 'lucide-react';
import type { ReasoningMetadata } from '../../../types/kyb.types';

interface ReasoningMetadataCardProps {
  metadata: ReasoningMetadata;
}

export const ReasoningMetadataCard: React.FC<ReasoningMetadataCardProps> = ({ metadata }) => {
  if (!metadata) return null;

  const {
    headquarters_determination,
    industry_determination,
    founded_year_determination
  } = metadata;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-5">
        <div className="flex items-center gap-3">
          <Brain className="w-7 h-7 text-white" />
          <div>
            <h3 className="text-2xl font-bold text-white">Data Determination Transparency</h3>
            <p className="text-indigo-100 text-sm">How key business attributes were determined</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {headquarters_determination && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <h4 className="font-bold text-slate-900">Headquarters Location</h4>
            </div>
            <p className="text-sm text-slate-700 mb-2">{headquarters_determination.explanation}</p>
            <p className="text-xs text-slate-600">Method: {headquarters_determination.method}</p>
            {headquarters_determination.notes && (
              <p className="text-xs text-slate-500 mt-1">Note: {headquarters_determination.notes}</p>
            )}
          </div>
        )}

        {industry_determination && (
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h4 className="font-bold text-slate-900">Industry Classification</h4>
            </div>
            <p className="text-sm text-slate-700 mb-2">{industry_determination.explanation}</p>
            <p className="text-xs text-slate-600">Method: {industry_determination.method}</p>
          </div>
        )}

        {founded_year_determination && (
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h4 className="font-bold text-slate-900">Founded Year</h4>
            </div>
            <p className="text-sm text-slate-700 mb-2">{founded_year_determination.explanation}</p>
            <p className="text-xs text-slate-600">Method: {founded_year_determination.method}</p>
            {founded_year_determination.is_approximate && (
              <p className="text-xs text-purple-600 mt-1">* Approximate date</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};